/** One parsed small PDF, retained briefly; callers must freshly verify its bytes. */
export function createPdfDocumentCache({ maxAgeMs = 120000, schedule = setTimeout, cancel = clearTimeout } = {}) {
    let cached;
    const destroy = (entry) => {
        if (entry.destroyed) return;
        entry.destroyed = true;
        cancel(entry.timer);
        entry.task.destroy().catch(() => {});
    };
    const retire = (entry) => {
        entry.keep = false;
        cancel(entry.timer);
        if (!entry.users) destroy(entry);
    };
    return {
        clear() { if (cached) retire(cached); cached = undefined; },
        acquire(hash, createTask) {
            let entry = hash && cached?.hash === hash && !cached.destroyed ? cached : null;
            const reused = Boolean(entry);
            if (!entry) {
                if (cached) retire(cached);
                entry = { hash, task: createTask(), users: 0, ready: false, keep: Boolean(hash), destroyed: false };
                cached = hash ? entry : undefined;
                entry.task.promise.then((document) => {
                    entry.ready = true;
                    if (document.numPages > 20) {
                        entry.keep = false;
                        if (cached === entry) cached = undefined;
                    }
                }, () => {
                    if (cached === entry) cached = undefined;
                    destroy(entry);
                });
            }
            entry.users++;
            cancel(entry.timer);
            let owned = true, claimed = false;
            const release = async () => {
                if (!owned) return;
                owned = false;
                entry.users--;
                if (entry.users) return;
                if (entry.keep && entry.ready && !entry.destroyed) {
                    entry.timer = schedule(() => {
                        if (cached === entry) cached = undefined;
                        retire(entry);
                    }, maxAgeMs);
                } else {
                    if (cached === entry) cached = undefined;
                    retire(entry);
                }
            };
            const handle = Object.freeze({ promise: entry.task.promise, destroy: release });
            return Object.freeze({
                reused, ready: entry.task.promise,
                take() { if (claimed || !owned) return; claimed = true; return handle; },
                cancel() { if (!claimed) return release(); },
                discard() { if (cached === entry) cached = undefined; retire(entry); },
            });
        },
    };
}
